import { isntNil } from "./any"

const precision3Format = new Intl.NumberFormat("en-US", {
  maximumFractionDigits: 3,
  minimumFractionDigits: 0,
})

const USDformatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
})

const integerFormatter = new Intl.NumberFormat("en-US", {
  maximumFractionDigits: 0,
})

/**
 * Collection of pure value formatting functions.
 */
export const Format = {
  /**
   * ------------------------------------------------------------------------
   * Numeric & Currency Formatting
   * ------------------------------------------------------------------------
   */

  /**
   * @param n
   * @returns a string representing the formatted number in the current runtime locale
   * @example
   * Format.number(1234.567) // "1,234.567" (in en-US locale)
   * Format.number(1234.567) // "1 234,567" (in fr-FR locale)
   * Format.number(null | undefined) // "0"
   */
  number(n: number | null | undefined) {
    return new Intl.NumberFormat(undefined).format(n ?? 0)
  },

  /**
   *
   * @param x a weight in pounds
   * @returns as a string formatted with pound units
   */
  lbs(x: number) {
    return precision3Format.format(x) + " lbs"
  },

  /**
   *
   * @param x a weight in ounces
   * @returns as a string formatted with ounce units
   */
  oz(x: number) {
    return precision3Format.format(x) + " oz"
  },

  /**
   * @param x
   * @returns x as string formatted as USD currency
   */
  usd(x: number) {
    return USDformatter.format(x)
  },

  /**
   * @param x an amount to format
   * @param units a units suffix to append
   * @returns x as string formatted as an integer with the given units suffix
   */
  customCurrency(x: number, units: string) {
    return `${integerFormatter.format(x)} ${units}`
  },

  /**
   * @param x an amount to format
   * @param units a units suffix to append. If provided, amount will be formatted using this value; otherwise
   *              will default to intl formatting
   * @param intlLocale amount will be formatted to this locale if units is not provided. Defaults to "en-US".
   * @param intlCurrency amount will be formatted to this currency if units is not provided. Defaults to "USD".
   * @returns x as string formatted as an integer with the given units suffix, or as currency using intl options
   */
  customCurrencyOrIntl({
    x,
    units,
    intlLocale,
    intlCurrency,
  }: {
    x: number
    units?: string
    intlLocale?: string
    intlCurrency?: string
  }) {
    const locale = intlLocale || "en-US"
    const currency = intlCurrency || "USD"

    return units
      ? Format.customCurrency(x, units)
      : Intl.NumberFormat(locale, { style: "currency", currency }).format(x)
  },

  /**
   * @param x an amount to format
   * @param units a units suffix to append
   * @returns x as string formatted as an integer with the given units if provided
   */
  currency(x: number, units?: string) {
    return isntNil(units) ? Format.customCurrency(x, units) : Format.usd(x)
  },

  /**
   * ------------------------------------------------------------------------
   * Strings Formatting
   * ------------------------------------------------------------------------
   */

  /**
   * @param count the number to determine pluralization for
   * @param singular the singular form of the word
   * @param plural the plural form of the word
   * @returns the appropriate singular or plural form based on the count
   */
  pluralize(count: number, singular: string, plural?: string) {
    return count === 1 ? singular : (plural ?? `${singular}s`)
  },

  /**
   * @param str the string to be titlecased
   *  naive implementation as it uppercases the 1st letter of each word
   * @returns the titlecased string
   */
  titlecase(str: string): string {
    return str.replace(/\b\w/g, (l) => l.toUpperCase())
  },
} as const
